import React, { useEffect, useState } from 'react';
import {
  getPolicyDocuments,
  uploadPolicyDocuments,
  deletePolicyDocument,
  downloadPolicyDocument,
} from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';
import '../../styles/policy-maturity.css';

const SCORE_FIELDS = [
  ['frameworks_score', 'Frameworks'],
  ['coverage_score', 'Coverage'],
  ['specificity_score', 'Specificity'],
  ['governance_score', 'Governance'],
  ['execution_score', 'Execution'],
];

function PolicyMaturity() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deletingDocument, setDeletingDocument] = useState(false);

  // Fetch documents on component mount
  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const docs = await getPolicyDocuments();
      setDocuments(docs);
    } catch (err) {
      setError('Failed to load policy documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDocuments = (files) => {
    if (documents.length > 0) {
      setError('Delete the existing policy document before uploading a replacement');
      return;
    }

    const fileArray = Array.from(files);
    const pdfFiles = fileArray.filter(f => f.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      setError('Only PDF files are accepted');
      return;
    }

    if (pdfFiles.length < fileArray.length) {
      setError(`${fileArray.length - pdfFiles.length} non-PDF file(s) were skipped`);
    }

    if (pdfFiles.length > 1) {
      setError('Only one policy document can be uploaded per company');
    }

    setPendingDocuments(
      pdfFiles.slice(0, 1).map(file => ({
        file,
        fileName: file.name,
        fileSize: file.size,
      }))
    );
  };

  const handleDocumentUpload = (e) => {
    handleDocuments(e.target.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleDocuments(e.dataTransfer.files);
  };

  const removePendingDocument = (index) => {
    setPendingDocuments(pendingDocuments.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async () => {
    if (pendingDocuments.length === 0) {
      setError('Please select at least one document to upload');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      await uploadPolicyDocuments(pendingDocuments);
      setSuccess('Uploaded policy document. A policy scan is queued.');
      setPendingDocuments([]);
      await loadDocuments();
    } catch (err) {
      setError('Failed to upload documents. Please try again.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete || deletingDocument) return;

    try {
      setDeletingDocument(true);
      await deletePolicyDocument(documentToDelete.id);
      setSuccess('Document deleted successfully');
      setDocumentToDelete(null);
      await loadDocuments();
    } catch (err) {
      setError('Failed to delete document');
      console.error(err);
    } finally {
      setDeletingDocument(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Extract S3 key from file_url (remove the API path prefix)
      const s3Key = doc.file_url.replace('/api/company/policy-documents/download/', '');
      await downloadPolicyDocument(decodeURIComponent(s3Key), doc.filename);
    } catch (err) {
      setError('Failed to download document');
      console.error(err);
      setTimeout(() => setError(''), 5000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const hasAnalysis = (doc) => SCORE_FIELDS.some(([key]) => doc[key] != null) || doc.penalty != null;

  const getPolicyScore = (doc) => {
    if (!hasAnalysis(doc)) return null;
    return SCORE_FIELDS.reduce((total, [key]) => total + Number(doc[key] || 0), 0);
  };

  const getScanStatus = (doc) => {
    if (doc.latest_scan_id && !doc.scan_executed_at) {
      return {
        label: hasAnalysis(doc) ? 'Rescan queued' : 'Queued',
        className: 'status-queued',
        icon: 'schedule',
      };
    }

    if (hasAnalysis(doc)) {
      return {
        label: 'Analyzed',
        className: 'status-analyzed',
        icon: 'task_alt',
      };
    }

    if (doc.scan_executed_at && !hasAnalysis(doc)) {
      return {
        label: 'Scan incomplete',
        className: 'status-error',
        icon: 'error',
      };
    }

    return {
      label: 'Not queued',
      className: 'status-muted',
      icon: 'radio_button_unchecked',
    };
  };

  const analyzedDocuments = documents.filter(hasAnalysis);
  const queuedDocuments = documents.filter((doc) => doc.latest_scan_id && !doc.scan_executed_at);
  const latestAnalyzed = analyzedDocuments[0] || null;
  const latestPolicyScore = latestAnalyzed ? getPolicyScore(latestAnalyzed) : null;
  const hasExistingDocument = documents.length > 0;

  return (
    <div className="policy-maturity-container">
      <div className="policy-maturity-header">
        <div>
          <h1>Policy Maturity</h1>
          <p>Upload cybersecurity policy PDFs and review the latest AI scan results.</p>
        </div>
        <button
          type="button"
          className="btn-secondary-policy"
          onClick={loadDocuments}
          disabled={loading}
        >
          <span className="material-symbols-outlined">refresh</span>
          Refresh
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="policy-summary-grid">
        <div className="policy-summary-item">
          <span>Documents</span>
          <strong>{documents.length}</strong>
        </div>
        <div className="policy-summary-item">
          <span>Queued scans</span>
          <strong>{queuedDocuments.length}</strong>
        </div>
        <div className="policy-summary-item">
          <span>Latest policy score</span>
          <strong>{latestPolicyScore == null ? 'N/A' : `${latestPolicyScore}/100`}</strong>
        </div>
        <div className="policy-summary-item">
          <span>Latest penalty</span>
          <strong>{latestAnalyzed?.penalty == null ? 'N/A' : `-${latestAnalyzed.penalty}`}</strong>
        </div>
      </div>

      {/* Upload Section */}
      <div className="policy-section">
        <h2>Upload Documents</h2>
        <div 
          className={`upload-area ${dragActive ? 'drag-active' : ''} ${hasExistingDocument ? 'is-disabled' : ''}`.trim()}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".pdf"
            onChange={handleDocumentUpload}
            id="file-input"
            className="file-input"
            disabled={uploading || hasExistingDocument}
          />
          <label htmlFor="file-input" className="file-upload-label">
            <span className="material-symbols-outlined">cloud_upload</span>
            <p>{hasExistingDocument ? 'Policy document already uploaded' : 'Click to upload or drag and drop'}</p>
            <small>{hasExistingDocument ? 'Delete the existing document to upload a replacement' : 'One PDF file only'}</small>
          </label>
        </div>

        {/* Pending Documents to Upload */}
        {pendingDocuments.length > 0 && (
          <div className="pending-documents">
            <h4>Ready to Upload ({pendingDocuments.length})</h4>
            {pendingDocuments.map((doc, index) => (
              <div key={index} className="document-item">
                <span className="material-symbols-outlined">description</span>
                <div className="document-info">
                  <p className="document-name">{doc.fileName}</p>
                  <small>{formatFileSize(doc.fileSize)}</small>
                </div>
                <button
                  onClick={() => removePendingDocument(index)}
                  className="btn-remove"
                  disabled={uploading}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            ))}
            <button
              onClick={handleUploadSubmit}
              className="btn-primary-policy"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Documents'}
            </button>
          </div>
        )}
      </div>

      {/* Documents List Section */}
      <div className="policy-section">
        <h2>Uploaded Documents</h2>
        {loading ? (
          <div className="loading-state">
            <span className="material-symbols-outlined spin">settings</span>
            <p>Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <span className="material-symbols-outlined">description</span>
            <p>No documents uploaded yet</p>
            <small>Upload your first policy document to get started</small>
          </div>
        ) : (
          <div className="document-list">
            {documents.map((doc) => (
              <article key={doc.id} className="policy-document-card">
                <div className="policy-document-card__header">
                  <span className="material-symbols-outlined">description</span>
                  <div className="document-info">
                    <p className="document-name">{doc.filename}</p>
                    <small>
                      Uploaded {formatDate(doc.uploaded_at)}
                      {doc.analyzed_at ? ` · Analyzed ${formatDate(doc.analyzed_at)}` : ''}
                    </small>
                  </div>
                  <div className={`policy-status ${getScanStatus(doc).className}`}>
                    <span className="material-symbols-outlined">{getScanStatus(doc).icon}</span>
                    {getScanStatus(doc).label}
                  </div>
                  <div className="document-actions">
                    <button
                      onClick={() => handleDownloadDocument(doc)}
                      className="btn-action"
                      disabled={loading}
                      title="Download"
                    >
                      <span className="material-symbols-outlined">download</span>
                    </button>
                    <button
                      onClick={() => setDocumentToDelete(doc)}
                      className="btn-remove"
                      disabled={loading}
                      title="Delete"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>

                {hasAnalysis(doc) ? (
                  <div className="policy-score-panel">
                    <div className="policy-score-total">
                      <span>Policy score</span>
                      <strong>{getPolicyScore(doc)}/100</strong>
                      <small>Penalty {doc.penalty == null ? 'N/A' : `-${doc.penalty}`}</small>
                    </div>
                    <div className="policy-score-breakdown">
                      {SCORE_FIELDS.map(([key, label]) => (
                        <div className="policy-score-row" key={key}>
                          <span>{label}</span>
                          <div className="policy-score-bar" aria-hidden="true">
                            <i style={{ width: `${Math.max(0, Math.min(20, Number(doc[key] || 0))) * 5}%` }} />
                          </div>
                          <strong>{doc[key] == null ? 'N/A' : `${doc[key]}/20`}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="policy-scan-placeholder">
                    <span className="material-symbols-outlined">hourglass_empty</span>
                    <p>
                      {doc.latest_scan_id
                        ? 'This document is waiting for the policy scan job to process it.'
                        : 'No policy scan has been queued for this document.'}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(documentToDelete)}
        title="Delete policy document?"
        description={`${documentToDelete?.filename || 'This document'} and its associated analysis will be permanently deleted.`}
        confirmLabel="Delete Document"
        loading={deletingDocument}
        onConfirm={handleDeleteDocument}
        onCancel={() => setDocumentToDelete(null)}
      />
    </div>
  );
}

export default PolicyMaturity;
